// Grocery list: the gap between the plan and what the user confirmed they have, by aisle.

import type { Plan } from './planner';
import { adaptForRules, ownedSet } from './ranking';
import type { DietRuleId } from './diet';
import { createStock, type StockItem } from './inventory';
import { getRecipe, type Unit } from './recipes';
import { substitutesFor } from './substitutions';
import { capitalize } from './text';
import { AISLE_LABELS, AISLE_ORDER, displayName, INGREDIENTS, type Aisle, type IngredientId } from './taxonomy';

export interface ListItem {
  key: string; // ingredient id, or `custom:<label>`
  ingredientId: IngredientId | null;
  label: string;
  qty: { amount: number; unit: Unit }[];
  aisle: Aisle;
  checked: boolean;
  source: 'plan' | 'manual';
  recipeIds: string[];
}

export interface ListContext {
  kitchen: IngredientId[];
  staples: IngredientId[];
  rules: DietRuleId[];
  dislikes: IngredientId[];
  /** Amounts the user entered for kitchen items; items without one count as enough. */
  stock?: StockItem[];
}

/**
 * Ingredients the plan needs that aren't owned (and can't be safely substituted), scaled to
 * servings. Owned items with a known amount are drawn down day by day, and any shortfall is listed.
 */
export function itemsForPlan(plan: Plan, ctx: ListContext): ListItem[] {
  const owned = ownedSet([...ctx.kitchen, ...ctx.staples]);
  const stock = createStock(ctx.stock ?? []);
  const byId = new Map<IngredientId, ListItem>();
  const add = (id: IngredientId, recipeId: string, amount?: number, unit?: Unit) => {
    const item = byId.get(id) ?? newItem(id, 'plan');
    if (amount !== undefined && unit) addQty(item, amount, unit);
    if (!item.recipeIds.includes(recipeId)) item.recipeIds.push(recipeId);
    byId.set(id, item);
  };

  const days = [...plan.entries].sort((a, b) => a.date.localeCompare(b.date));
  for (const entry of days) {
    if (entry.status !== 'planned') continue;
    const recipe = getRecipe(entry.recipeId);
    if (!recipe) continue;
    const adapted = adaptForRules(recipe, ctx.rules, ctx.dislikes);
    if (!adapted) continue;
    const scale = entry.servings / recipe.servings;
    for (const ing of adapted.ingredients) {
      if (ing.role === 'optional') continue;
      if (owned.has(ing.id)) {
        const short = stock.draw(ing, scale);
        if (short > 0) add(ing.id, recipe.id, short, ing.unit);
        continue;
      }
      if (substitutesFor(ing.id).some((s) => owned.has(s.to))) continue;
      add(ing.id, recipe.id, ing.qty !== undefined ? ing.qty * scale : undefined, ing.unit);
    }
  }
  return [...byId.values()];
}

/** How much of a plan's shopping is already covered by the kitchen and staples. */
export function planCoverage(plan: Plan, ctx: ListContext): { total: number; have: number; need: ListItem[] } {
  const all = new Set<IngredientId>();
  for (const entry of plan.entries) {
    if (entry.status !== 'planned') continue;
    const recipe = getRecipe(entry.recipeId);
    const adapted = recipe && adaptForRules(recipe, ctx.rules, ctx.dislikes);
    if (!adapted) continue;
    for (const ing of adapted.ingredients) {
      if (ing.role !== 'optional') all.add(ing.id);
    }
  }
  // Owned but short counts as needed, so have + need always equals the whole plan.
  const need = itemsForPlan(plan, ctx);
  const needIds = new Set(need.map((i) => i.ingredientId));
  return { total: all.size, have: [...all].filter((id) => !needIds.has(id)).length, need };
}

/** Every required ingredient of one recipe, scaled to servings: shopping for an occasion. */
export function itemsForRecipe(recipeId: string, servings: number, rules: DietRuleId[], dislikes: IngredientId[]): ListItem[] {
  const recipe = getRecipe(recipeId);
  const adapted = recipe && adaptForRules(recipe, rules, dislikes);
  if (!recipe || !adapted) return [];
  const scale = servings / recipe.servings;
  return adapted.ingredients.filter((i) => i.role !== 'optional').map((ing) => {
    const item = { ...newItem(ing.id, 'manual'), recipeIds: [recipeId] };
    if (ing.qty !== undefined && ing.unit) addQty(item, ing.qty * scale, ing.unit);
    return item;
  });
}

export function newItem(id: IngredientId, source: ListItem['source']): ListItem {
  return { key: id, ingredientId: id, label: displayName(id), qty: [], aisle: INGREDIENTS[id].aisle, checked: false, source, recipeIds: [] };
}

export function customItem(label: string): ListItem {
  const clean = label.trim().slice(0, 60);
  return { key: `custom:${clean.toLowerCase()}`, ingredientId: null, label: clean, qty: [], aisle: 'other', checked: false, source: 'manual', recipeIds: [] };
}

function addQty(item: ListItem, amount: number, unit: Unit) {
  const same = item.qty.find((q) => q.unit === unit);
  if (same) same.amount += amount;
  else item.qty.push({ amount, unit });
}

/**
 * Regenerate plan-derived items while keeping manual items and the user's check marks.
 * Checked plan items that are no longer needed are kept (the user may already have bought them).
 */
export function mergeList(existing: ListItem[], generated: ListItem[]): ListItem[] {
  const prev = new Map(existing.map((i) => [i.key, i]));
  const out: ListItem[] = [];
  const seen = new Set<string>();
  for (const g of generated) {
    const p = prev.get(g.key);
    out.push({ ...g, checked: p?.checked ?? false, source: p?.source === 'manual' ? 'manual' : 'plan' });
    seen.add(g.key);
  }
  for (const p of existing) {
    if (seen.has(p.key)) continue;
    if (p.source === 'manual' || p.checked) out.push(p);
  }
  return out;
}

export function groupByAisle(items: ListItem[]): { aisle: Aisle; items: ListItem[] }[] {
  return AISLE_ORDER.map((aisle) => ({
    aisle,
    items: items.filter((i) => i.aisle === aisle).sort((a, b) => Number(a.checked) - Number(b.checked) || a.label.localeCompare(b.label)),
  })).filter((g) => g.items.length > 0);
}

const FRACTIONS: [number, string][] = [[0.25, '¼'], [0.33, '⅓'], [0.5, '½'], [0.67, '⅔'], [0.75, '¾']];

export function formatAmount(n: number): string {
  const whole = Math.floor(n);
  const frac = n - whole;
  if (frac < 0.08) return String(whole || (n > 0 ? 1 : 0));
  if (frac > 0.92) return String(whole + 1);
  const f = FRACTIONS.reduce((best, cur) => (Math.abs(cur[0] - frac) < Math.abs(best[0] - frac) ? cur : best));
  return whole ? `${whole}${f[1]}` : f[1];
}

const UNIT_LABEL: Record<Unit, [string, string]> = {
  g: ['g', 'g'], ml: ['ml', 'ml'], cup: ['cup', 'cups'], tbsp: ['tbsp', 'tbsp'], tsp: ['tsp', 'tsp'],
  each: ['', ''], clove: ['clove', 'cloves'], slice: ['slice', 'slices'], can: ['can', 'cans'],
  pinch: ['pinch', 'pinches'], handful: ['handful', 'handfuls'],
};

export function formatQty(amount: number, unit: Unit): string {
  if (unit === 'g' || unit === 'ml') return `${Math.max(5, Math.round(amount / 5) * 5)}${unit}`;
  const [one, many] = UNIT_LABEL[unit];
  const label = amount > 1 ? many : one;
  return label ? `${formatAmount(amount)} ${label}` : formatAmount(amount);
}

export function formatItemQty(item: Pick<ListItem, 'qty'>): string {
  return item.qty.map((q) => formatQty(q.amount, q.unit)).join(' + ');
}

export type Store = 'instacart' | 'walmart' | 'target';
export const STORES: { id: Store; label: string }[] = [
  { id: 'instacart', label: 'Instacart' }, { id: 'walmart', label: 'Walmart' }, { id: 'target', label: 'Target' },
];

/** What to search a store for: the taxonomy's shopping name, or the user's own text. */
export function shopTerm(item: Pick<ListItem, 'ingredientId' | 'label'>): string {
  return item.ingredientId ? INGREDIENTS[item.ingredientId].shopAs ?? INGREDIENTS[item.ingredientId].name : item.label;
}

/** Plain product-search link at a store. No affiliate parameters. */
export function storeSearchUrl(store: Store, item: Pick<ListItem, 'ingredientId' | 'label'>): string {
  const q = encodeURIComponent(shopTerm(item));
  switch (store) {
    case 'instacart': return `https://www.instacart.com/store/s?k=${q}`;
    case 'walmart': return `https://www.walmart.com/search?q=${q}`;
    case 'target': return `https://www.target.com/s?searchTerm=${q}`;
  }
}

export const instacartSearchUrl = (item: Pick<ListItem, 'ingredientId' | 'label'>) => storeSearchUrl('instacart', item);

/**
 * A store link through an affiliate tracking template ("https://…?u={url}"), so purchases earn a
 * commission. A missing or malformed template (not https, no {url}) leaves the link as it is.
 */
export function withAffiliate(url: string, template: string | undefined): string {
  if (!template || !template.startsWith('https://') || !template.includes('{url}')) return url;
  return template.replace('{url}', encodeURIComponent(url));
}

/** Items for the shop-instacart function: search name, display text and amounts. */
export function shopRequestItems(items: ListItem[]) {
  return items.filter((i) => !i.checked).map((i) => ({
    name: shopTerm(i).slice(0, 80),
    display: `${capitalize(i.label)}${i.qty.length ? `, ${formatItemQty(i)}` : ''}`.slice(0, 80),
    qty: i.qty.map((q) => ({ amount: q.amount, unit: q.unit })),
  }));
}

export function listAsText(items: ListItem[]): string {
  return groupByAisle(items.filter((i) => !i.checked))
    .map((g) => [AISLE_LABELS[g.aisle], ...g.items.map((i) => `- ${i.label}${i.qty.length ? ` (${formatItemQty(i)})` : ''}`)].join('\n'))
    .join('\n\n');
}
